import type { DataSession } from "../../../../data/core/session";

import {
  artistProfileQueryKey,
  type ArtistProfileQueryData,
} from "@hooks/trpc/useArtists";
import type { CatalogCollectionType, CatalogSong } from "../../domain/types";
import { createLocalSongAssets } from "../localAudioPaths";

/**
 * Attempts to resolve songs from the React Query cache.
 * Returns undefined on cache miss so the caller can fall back to a network fetch.
 *
 * Applies local-audio-path substitution before returning, so that a queue
 * built from an in-memory cache hit plays from disk for downloaded songs —
 * matching the contract of the network-fetch path (`fetchSongsForCollection`).
 * Without this, a cache-hit playback would always use the remote URL even
 * when the file is sitting on disk, breaking offline playback whenever the
 * screen had been visited recently.
 */
export function createCollectionCache(data: DataSession) {
  const trpcUtils = data.utils;
  const queryClient = data.queryClient;
  const { substituteLocalSongAssets } = createLocalSongAssets(data.db);
  function tryGetSongsFromCache(
    collectionId: string,
    type: CatalogCollectionType,
  ): CatalogSong[] | undefined {
    if (!data.isCurrent()) return undefined;
    if (type === "album") {
      const cached = trpcUtils.catalog.songs.getByAlbumIds.getData([
        collectionId,
      ]);
      return cached && cached.length > 0
        ? substituteLocalSongAssets(cached)
        : undefined;
    }
    if (type === "playlist") {
      const cached = trpcUtils.catalog.songs.getByPlaylistIds.getData([
        collectionId,
      ]);
      return cached && cached.length > 0
        ? substituteLocalSongAssets(cached)
        : undefined;
    }
    if (type === "artist") {
      const profile = queryClient.getQueryData<ArtistProfileQueryData>(
        artistProfileQueryKey(collectionId),
      );
      return profile?.popularSongs && profile.popularSongs.length > 0
        ? substituteLocalSongAssets(profile.popularSongs)
        : undefined;
    }
    return undefined;
  }

  return { tryGetSongsFromCache };
}
